import UIComponent from "../ui/component";

export class DownloadDropdown extends UIComponent {
    init() {
        this.#rememberOriginalLinks();
        this.bindDownloadChoice();
        this.bindResultTabs();
    }

    #original = null;
    #bound = false;
    #choiceBound = false;
    #syncingChoice = false;

    #rememberOriginalLinks() {
        const dom = (name) =>
            this.element?.querySelector?.(`[data-tbx-name="${name}"]`) || null;

        this.#original = {
            text: this.linkText?.attr?.("href") || dom("linkText")?.getAttribute?.("href") || null,
            word: this.linkWord?.attr?.("href") || dom("linkWord")?.getAttribute?.("href") || null,
            excel: this.linkExcel?.attr?.("href") || dom("linkExcel")?.getAttribute?.("href") || null,
            srt: this.linkSrt?.attr?.("href") || dom("linkSrt")?.getAttribute?.("href") || null,
        };
    }

    bindResultTabs() {
        if (this.#bound) return;
        this.includeComponents({
            resultTabs: "document@#result-tabs",
        });

        if (!this.resultTabs) {
            requestAnimationFrame(() => this.bindResultTabs());
            return;
        }
        this.#bound = true;

        const initialTabId = this.resultTabs.getActiveTabId?.();
        this.updateForTab(initialTabId);
        this.#syncChoiceToTab(initialTabId);
        this.updateFormatVisibility(initialTabId);

        this.resultTabs.onChange?.((tabId) => {
            this.updateForTab(tabId);
            this.#syncChoiceToTab(tabId);
            this.updateFormatVisibility(tabId);
        });

        this.resultTabs?.on?.("aiContentUpdated", (event) => {
            const updatedId = event?.detail?.id;
            const activeId = this.resultTabs?.getActiveTabId?.();
            if (!updatedId || String(updatedId) !== String(activeId)) return;
            this.updateFormatVisibility(activeId);
        });
    }

    updateForTab(tabId) {
        const effectiveTabId = tabId ?? this.resultTabs?.getActiveTabId?.();
        const isOriginal = !effectiveTabId || String(effectiveTabId) === "0";

        if (isOriginal) {
            this.colExcel?.show?.();
            this.colSrt?.show?.();
            this.#setHref(this.linkText, this.#original?.text);
            this.#setHref(this.linkWord, this.#original?.word);
            this.#setHref(this.linkExcel, this.#original?.excel);
            this.#setHref(this.linkSrt, this.#original?.srt);
            this.#setHrefInRoot("linkText", this.#original?.text);
            this.#setHrefInRoot("linkWord", this.#original?.word);
            this.#setHrefInRoot("linkExcel", this.#original?.excel);
            this.#setHrefInRoot("linkSrt", this.#original?.srt);
            return;
        }

        this.colExcel?.hide?.();
        this.colSrt?.hide?.();

        const desktopTabEl =
            this.resultTabs?.element?.querySelector?.(
                `.result-tabs__item[data-id="${effectiveTabId}"]`
            ) || null;
        const mobileTabEl =
            this.resultTabs?.element?.querySelector?.(
                `.result-tabs__dropdown-item[data-id="${effectiveTabId}"]`
            ) || null;

        const tabEl =
            desktopTabEl || mobileTabEl || this.resultTabs?.getActiveTabElement?.() || null;

        const exportCarrier =
            tabEl?.querySelector?.(
                "[data-tab-export-text_url],[data-tab-export-word_url]"
            ) || tabEl;
        const explicitTextUrl =
            exportCarrier?.getAttribute?.("data-tab-export-text_url") ||
            tabEl?.getAttribute?.("data-tab-export-text_url") ||
            null;
        const explicitWordUrl =
            exportCarrier?.getAttribute?.("data-tab-export-word_url") ||
            tabEl?.getAttribute?.("data-tab-export-word_url") ||
            null;

        if (explicitTextUrl || explicitWordUrl) {
            this.#setHref(this.linkText, explicitTextUrl);
            this.#setHref(this.linkWord, explicitWordUrl);
            this.#setHrefInRoot("linkText", explicitTextUrl);
            this.#setHrefInRoot("linkWord", explicitWordUrl);
            return;
        }

        const updateUrl =
            tabEl?.getAttribute?.("data-tab-update-url") ||
            desktopTabEl?.getAttribute?.("data-tab-update-url") ||
            mobileTabEl?.getAttribute?.("data-tab-update-url") ||
            null;
        const textUrl = this.#toVariantExportUrl(updateUrl, "text");
        const wordUrl = this.#toVariantExportUrl(updateUrl, "word");
        if (textUrl || wordUrl) {
            this.#setHref(this.linkText, textUrl);
            this.#setHref(this.linkWord, wordUrl);
            this.#setHrefInRoot("linkText", textUrl);
            this.#setHrefInRoot("linkWord", wordUrl);
        }
    }

    #withExportSettings(href) {
        if (!href || href === "#") return href;

        const sample = document.querySelector?.(".actions-export[href]") || null;
        if (!sample?.href) return href;

        try {
            const sampleUrl = new URL(sample.href);
            const settingsKeys = [
                "speakers",
                "timestamps",
                "maxLineCount",
                "maxLineWidth",
            ];

            const url = new URL(href, window.location.origin);
            for (const key of settingsKeys) {
                const val = sampleUrl.searchParams.get(key);
                if (val !== null) {
                    url.searchParams.set(key, val);
                } else {
                    url.searchParams.delete(key);
                }
            }
            return url.toString();
        } catch (_) {
            return href;
        }
    }

    #setHrefInRoot(tbxName, href) {
        if (!tbxName || !href) return;
        href = this.#withExportSettings(href);
        const el = this.element?.querySelector?.(`[data-tbx-name="${tbxName}"]`);
        if (el?.setAttribute) el.setAttribute("href", href);
    }

    bindDownloadChoice() {
        if (this.#choiceBound) return;
        this.includeComponents({
            downloadChoice: '[data-tbx-name="DownloadChoice"]',
        });

        if (!this.downloadChoice?.onChange) {
            requestAnimationFrame(() => this.bindDownloadChoice());
            return;
        }

        this.#choiceBound = true;
        this.downloadChoice.onChange((tabId) => {
            if (this.#syncingChoice) return;
            this.updateForTab(tabId);
        });
    }

    #syncChoiceToTab(tabId) {
        if (this.#syncingChoice) return;
        if (!this.downloadChoice?.setSelectedTab) return;
        this.#syncingChoice = true;
        try {
            this.downloadChoice.setSelectedTab(String(tabId));
        } finally {
            this.#syncingChoice = false;
        }
    }

    #setHref(tbxEl, href) {
        if (!tbxEl || !href) return;
        href = this.#withExportSettings(href);
        if (typeof tbxEl.attr === "function") {
            tbxEl.attr("href", href);
            try {
                tbxEl.element?.setAttribute?.("href", href);
            } catch (_) {}
            return;
        }
        if (typeof tbxEl.el === "function") {
            const el = tbxEl.el?.();
            el?.setAttribute?.("href", href);
            return;
        }

        if (tbxEl instanceof HTMLElement) {
            tbxEl.setAttribute("href", href);
            return;
        }
        if (tbxEl.element?.setAttribute) {
            tbxEl.element.setAttribute("href", href);
        }
    }

    #toVariantExportUrl(apiUrl, type) {
        if (!apiUrl) return null;
        const isAbsolute = apiUrl.includes("://");
        const webBase = isAbsolute
            ? apiUrl.replace(/\/api\//, "/")
            : apiUrl.replace(/^\/api\//, "/");
        return `${webBase}/export?type=${encodeURIComponent(type)}`;
    }

    updateFormatVisibility(tabId) {
        const isPending = this.#isTabPending(tabId);
        const shouldShow = !isPending;
        this.#setVisibility(this.colText, shouldShow);
        this.#setVisibility(this.colWord, shouldShow);
        this.#setVisibility(this.formatLabel, shouldShow);
    }

    #isTabPending(tabId) {
        const id = tabId ?? this.resultTabs?.getActiveTabId?.();
        if (!id) return false;
        const content =
            this.resultTabs?.getCopyContainer?.(id) ||
            document.querySelector?.(`[data-tab-content="${id}"]`) ||
            null;
        if (!content) return false;
        const preloader = content.querySelector?.(".ai_preloader");
        if (!preloader) return false;
        return !preloader.classList.contains("d-none");
    }

    #setVisibility(el, isVisible) {
        if (!el) return;
        if (el.show && el.hide) {
            isVisible ? el.show() : el.hide();
            return;
        }
        const node = el.el ? el.el() : el.element || el;
        if (!node || !node.classList) return;
        node.classList.toggle("d-none", !isVisible);
    }

}


